import { all, call, delay, put, select, spawn, takeEvery, takeLatest } from 'redux-saga/effects';

import { actions as globalActions, types as globalTypes } from '@elab/core/store/global';
import { actions as uiActions } from '@elab/core/store/ui';
import dynaForms from '@elab/core/utils/dynaForms';
import { createWatcher } from '@elab/core/store/core/saga';
import analytics from '@elab/core/utils/analytics';

import api from './api';
import actions from './actions';
import selectors from './selectors';
import types from './types';

const buildDto = (preferences, consentTemplatesData) => {
    const consentTemplates = consentTemplatesData.map(consent => {
        const { value, radioValue } = preferences[consent.id];
        consent.currentConsent = {};
        consent.currentConsent.active = value;

        if (value && radioValue) {
            consent.currentConsent.value = radioValue;
        }

        return consent;
    });

    return { consentTemplates };
};

function* watchDoSubmit() {
    yield takeLatest(types.DO_SUBMIT, function*({ payload = {} }) {
        const { submitDelay, ignoreSuccessMessage } = payload;
        if (submitDelay) {
            // delay preventing too many requests
            yield delay(submitDelay);
        }
        yield put(globalActions.resetFormsMessages());
        const preferences = yield select(selectors.getPreferences);
        const consentTemplates = yield select(selectors.getConsentTemplates);
        yield spawn(analytics.handlePrivacySettingsSubmit);

        try {
            yield call(api.saveConsents, buildDto(preferences, consentTemplates));
            const { label, action } = yield select(selectors.getCurrentSelection);
            yield spawn(analytics.handlePrivacySettingsComplete, {
                label,
                action: action ? 'toggle on' : 'toggle off',
            });
            if (!ignoreSuccessMessage) {
                yield put(actions.setGlobalSuccess());
                yield put(uiActions.scrollToCenter());
            }
            yield put(actions.resetData());
        } catch (e) {
            yield call(dynaForms.showErrors, e.json && e.json.errors, {});
        }
    });
}

function* watchResetData() {
    yield takeEvery([types.AFTER_INITIALIZE, types.RESET_DATA], function*() {
        const [groups = {}, templates = {}] = yield all([
            call(api.getConsentGroups),
            call(api.getConsentTemplates),
        ]);

        const { consentTemplates = [] } = templates;
        const preferenceGroups = groups.consentTemplateGroups
            .map(group => ({
                ...group,
                items: consentTemplates.filter(({ groupId }) => groupId === group.id),
            }))
            .sort((group1, group2) => group1.order - group2.order);

        const otherGroup = {
            items: consentTemplates.filter(({ groupId }) => !groupId),
        };

        const preferences = templates.consentTemplates.reduce(
            (acc, cur = {}) => ({
                ...acc,
                [cur.id]: {
                    switchOn: cur.switchOn || [],
                    switchOff: cur.switchOff || [],
                    value: (cur.currentConsent && cur.currentConsent.active) || false,
                    radioValue:
                        (cur.currentConsent && cur.currentConsent.value) || cur.defaultValue || '',
                },
            }),
            {},
        );

        yield put(actions.setPreferenceGroups(Object.values(preferenceGroups)));
        yield put(actions.setPreferences(preferences));
        yield put(actions.setOtherGroup(otherGroup));
        yield put(actions.setConsentTemplates(consentTemplates));
    });
}

export default storeName => () => [
    ...dynaForms.createSagaFor(storeName),
    watchResetData(),
    watchDoSubmit(),
    createWatcher(globalTypes.RESET_FORMS_MESSAGES, actions.resetGlobalMessage()),
];
